import React from 'react'
import { WikiArticle, WikiArticleMain, WikiArticleSubPanel } from '../WikiArticle'
import { Heading, ListItem, Text } from '@chakra-ui/react'
import { LinkToSection, SectionIndex, SectionTitle } from '../../../new_component/organisms/SectionIndex'
import CodeExample from '../../../new_component/organisms/CodeExample'

const Endpoints = () => {
	return <WikiArticle>
		<WikiArticleMain>
			<section>
				<Heading as="h2">API Endpoints</Heading>
				<Text>
					Application Base exposes a RESTful API layer that allows seamless interaction with your FQL-defined Forms.
					These endpoints let you query, mutate, and automate workflows across your Application Base, while preserving
					business logic and data integrity.
				</Text>
				<Text>
					This section outlines the key endpoints, their purposes, and best practices for integrating them in your apps.
				</Text>
			</section>

			<section>
				<SectionTitle>
					User Management
				</SectionTitle>
				<>
					<Text fontWeight={700}>
						Guest Token
					</Text>
					<Text>
						Most of the requests to the API must contain a token. If the user doesn't want to create a new account,
						it is possible to test the functionalities with a guest token. This endpoint provides a guest token but
						the data is not persistent.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/auth/register/guest
					</Text>
					<Text>
						<b>	- Parameters:</b> application_id (optional): ID of the application
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "New guest created",
	"token": "guest_token"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Login
					</Text>
					<Text>
						This endpoint allows the user to log in by sending the email address and the password (hashed, for more security).
						It returns the basic user information such as email, token and nickname.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/login
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								email: User's email address
							</li>
							<li>
								password: User's password
							</li>
							<li>
								application_id (optional): ID of the application
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Log in successfully",
	"token": "user_token",
	"nickname": "user_nickname",
	"email": "user_email"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Register Account
					</Text>
					<Text>
						This endpoint allows the user to register by sending just an email address. It returns an
						activattion token that is used to complete registration later
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/auth/register
					</Text>
					<Text>
						<b>	- Parameters:</b> email: User's email address
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Signed up successfully",
	"activation_token": "token"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Activate Account
					</Text>
					<Text>
						This endpoint allows the user to complete the registration by activating the account.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/activate_account
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								email: User's email address
							</li>
							<li>
								password: User's password
							</li>
							<li>
								activation_token: Token for the activation received from /register endpoint
							</li>
							<li>
								nickname: User's nickname
							</li>
							<li>
								application_id (optional): ID of the application
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Account activated successfully",
	"token": "user_token"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Check Account Activation Status
					</Text>
					<Text>
						This endpoint returns a message to know if the account is activated or not.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/activated
					</Text>
					<Text>
						<b>	- Parameters: </b>
						email: User's email address
					</Text>

					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\

{
	"msg": "The account is/isn't activated"
}
              `}
						</CodeExample>
					</Text></>
			</section>

			<section>
				<SectionTitle>
					Password Management
				</SectionTitle>

				<>
					<Text fontWeight={700}>
						Password Reset Request
					</Text>
					<Text>
						This endpoint allows the user to reset the password. It returns a reset token
						that allows the user to confirm the request and reset the password.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/password_reset/request
					</Text>
					<Text>
						<b>	- Parameters:</b> email: User's email address
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "New password request",
	"token": "reset_token"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Confirm Password Reset
					</Text>
					<Text>
						This endpoint allows the user to confirm the password reset request.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/password_reset/confirm
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: Reset token received from request
							</li>
							<li>
								new_password: User's new password
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Password changed successfully"
}
              `}
						</CodeExample>
					</Text></>
			</section>

			<section>
				<SectionTitle>
					Organization Management
				</SectionTitle>

				<>
					<Text fontWeight={700}>
						Activate Invitation
					</Text>
					<Text>
						This endpoint allows the user to activate an invitation once the user is invited to collaborate.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/activate_invitation
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								email or nickname: User identifier
							</li>
							<li>
								group: Group name
							</li>
							<li>
								organization: Organization name
							</li>
							<li>
								activation_token: Invitation token
							</li>
							<li>
								owner_email: Organization owner email address
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Invitation to Group {group} successfully activated"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Reject Invitation
					</Text>
					<Text>
						This endpoint allows the user to reject an invitation once the user is invited to collaborate.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/reject_invitation
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								email or nickname: User identifier
							</li>
							<li>
								group: Group name
							</li>
							<li>
								organization: Organization name
							</li>
							<li>
								activation_token: Invitation token
							</li>
							<li>
								owner_email: Organization owner email address
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Invitation to Group {group} rejected"
}
              `}
						</CodeExample>
					</Text></>
			</section>

			<section>
				<SectionTitle>Application Management</SectionTitle>

				<>
					<Text fontWeight={700}>
						Create Application
					</Text>
					<Text>
						This endpoint allows the user to create an application. An application is a set of commands that are prevoiusly saved on the API
						and when an user wants to access or is invited to that application, a formerly created session of the connection pool is assigned to it.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/create_application
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: User's authentication token
							</li>
							<li>
								name: Application name
							</li>
							<li>
								commands: List of commands
							</li>
							<li>
								pool_size: Connection pool size (default: 10)
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Application {name} created",
	"application_id": "app_id"
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Remove Application
					</Text>
					<Text>
						This endpoint allows the user to remove a prevoiusly created application.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/remove_application
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: User's authentication token
							</li>
							<li>
								name: Application name
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Application {name} removed"
}
              `}
						</CodeExample>
					</Text>
				</>
				<>
					<Text fontWeight={700}>
						Application Commands
					</Text>
					<Text>
						This endpoint allows the user to get all the commands of a prevoiusly created application.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/application/commands
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: User's authentication token
							</li>
							<li>
								application_id: Application unique identifier
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Application commands",
	"commands": ["command1", "command2", ...]
}
              `}
						</CodeExample>
					</Text>
				</>
			</section>

			<section>
				<SectionTitle>Code Execution</SectionTitle>

				<>
					<Text fontWeight={700}>
						Run Code
					</Text>
					<Text>
						This endpoint allows the user to run FQL code.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/run_code
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: User's authentication token
							</li>
							<li>
								code: Code to execute
							</li>
							<li>
								fql_token: FQL token information
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response: </b>It varies depending on the code executed.
					</Text></>

				<>
					<Text fontWeight={700}>
						Run Commands
					</Text>
					<Text>
						This endpoint allows the user to run a list of FQL commands.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/run_commands
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>
								token: User's authentication token
							</li>
							<li>
								commands: List of commands to execute
							</li>
							<li>
								fql_token: FQL token information
							</li>
						</ul>
					</Text>
					<Text>
						<b> - Response: </b>It varies depending on the code executed.
					</Text></>
			</section>

			<section>
				<SectionTitle>
					Database Management
				</SectionTitle>

				<>
					<Text fontWeight={700}>
						Reset Schema
					</Text>
					<Text>
						This endpoint erase all the information of the user.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/reset_schema
					</Text>
					<Text>
						<b>	- Parameters:</b> token: User's authentication token
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"msg": "Schema reseted"
}
              `}
						</CodeExample>
					</Text></>

			</section>

			<section>
				<SectionTitle>Connection Management</SectionTitle>
				<>
					<Text fontWeight={700}>
						Connect
					</Text>
					<Text>
						This endpoint allows the user to stablish a connection with different FQL engines.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/connect
					</Text>
					<Text>
						<b>	- Parameters:</b> None
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"ok": "ok",
	"msg": "Connected"
}
              `}
						</CodeExample>
					</Text></>
			</section>

			<section>
				<SectionTitle>Code Completion</SectionTitle>
				<>
					<Text fontWeight={700}>
						Autocomplete
					</Text>
					<Text>
						This endpoint is designed to give autocompletion suggestions for FQL syntax.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/autocomplete
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>token: User's authentication token</li>
							<li>code: Code to get suggestions for</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"suggestions": ["suggestion1", "suggestion2", ...]
}
              `}
						</CodeExample>
					</Text></>

				<>
					<Text fontWeight={700}>
						Get Form Data Labels
					</Text>
					<Text>
						This endpoint is designed to allow the user to get all the labels of a previously created form.
					</Text>
					<Text>
						<b> - Endpoint:</b> POST /api/get-form-data-labels
					</Text>
					<Text>
						<b>	- Parameters:</b>
						<ul style={{ textIndent: "20px" }}>
							<li>token: User's authentication token</li>
							<li>form_name: Name of the form</li>
						</ul>
					</Text>
					<Text>
						<b> - Response:</b>
						<CodeExample margin={0} title="JSON" run={false}>
							{`\
{
	"data_labels": ["label1", "label2", ...]
}
              `}
						</CodeExample>
					</Text></>
			</section>

			<section>
				<SectionTitle>Error Handling</SectionTitle>
				<Text>All endpoints follow a consistent error response format:</Text>
				<CodeExample margin={0} title="JSON" run={false}>
					{`\
{
	"msg": "ERROR - error message",
	"code": "",
	"output": null,
	"fql_token": null,
	"error": true,
	"verbose": ""
}
              `}
				</CodeExample>
			</section>

			<section>
				<SectionTitle>
					CORS
				</SectionTitle>
				<Text>
					The API supports CORS with the following headers:
				</Text>
				<ul>
					<li>Access-Control-Allow-Headers: *</li>
					<li>Access-Control-Allow-Origin: *</li>
					<li>Access-Control-Allow-Methods: *</li>
					<li>Access-Control-Max-Age: 86400</li>
				</ul>
			</section>

			<section>
				<SectionTitle>
					Timeout
				</SectionTitle>
				<Text>
					In production environment, requests are limited to 10 seconds.
					Requests exceeding this limit will return a 500 status code with a "Process timeout" message.
				</Text>
			</section>


		</WikiArticleMain >
		<WikiArticleSubPanel>
			<SectionIndex>
				<ListItem><LinkToSection>API Endpoints</LinkToSection></ListItem>
				<ListItem><LinkToSection>User Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Password Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Organization Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Application Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Code Execution</LinkToSection></ListItem>
				<ListItem><LinkToSection>Database Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Connection Management</LinkToSection></ListItem>
				<ListItem><LinkToSection>Code Completion</LinkToSection></ListItem>
				<ListItem><LinkToSection>Error Handling</LinkToSection></ListItem>
				<ListItem><LinkToSection>CORS</LinkToSection></ListItem>
				<ListItem><LinkToSection>Timeout</LinkToSection></ListItem>
			</SectionIndex>
		</WikiArticleSubPanel>
	</WikiArticle >
}

export default Endpoints